import React, { useState } from 'react';
import { useTable, usePagination } from 'react-table';
import { Button, Table, Row, Col, Badge, Card } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';

const ReceiptsTable = ({ receipts }) => {
  const [recordsPerPage, setRecordsPerPage] = useState(10); // Default to 10 records per page

  const formatDate = (date) => format(new Date(date), 'dd/MM/yyyy');

  const columns = React.useMemo(
    () => [
      {
        Header: '#',
        Cell: ({ row }) => row.index + 1, // Record number
      },
      { Header: 'Date', accessor: 'rdate', Cell: ({ value }) => formatDate(value) },
      { Header: 'Receipt No.', accessor: 'receiptno' },
      { Header: 'Amount', accessor: 'amount', Cell: ({ value }) => `₹${value.toFixed(2)}` },
      { Header: 'Patient Name', accessor: 'pname' },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: receipts,
      initialState: { pageIndex: 0, pageSize: recordsPerPage }, // Default page size set to 10
    },
    usePagination
  );

  // Calculate the sum for the current page
  const pageSum = page.reduce((sum, row) => sum + parseFloat(row.values.amount || 0), 0);

  // Calculate the sum for all records
  const totalSum = receipts.reduce((sum, record) => sum + parseFloat(record.amount || 0), 0);

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(receipts);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Receipts');
    XLSX.writeFile(wb, 'receipts.xlsx');
  };

  const exportToPdf = () => {
    const doc = new jsPDF();
    doc.autoTable({ html: '#receipts-table' });
    doc.save('receipts.pdf');
  };

  return (
    <div className="mt-5">
      <Card>
        <Card.Body>
          <Card.Title>Receipts</Card.Title>
          <Row className="mb-3">
            <Col>
              <Button onClick={exportToExcel} className="me-2">
                Export to Excel
              </Button>
              <Button onClick={exportToPdf}>Export to PDF</Button>
            </Col>
            <Col className="text-end">
              <div>
                <strong>Page Total:</strong> <Badge bg="success">₹{pageSum.toFixed(2)}</Badge>
              </div>
              <div>
                <strong>Overall Total:</strong> <Badge bg="primary">₹{totalSum.toFixed(2)}</Badge>
              </div>
            </Col>
          </Row>
          <Table {...getTableProps()} id="receipts-table" className="table-striped table-bordered table-hover">
            <thead className="table-dark">
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <Row className="mt-3">
            <Col>
              <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                {'<<'}
              </Button>{' '}
              <Button onClick={previousPage} disabled={!canPreviousPage}>
                {'<'}
              </Button>{' '}
              <Button onClick={nextPage} disabled={!canNextPage}>
                {'>'}
              </Button>{' '}
              <Button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}>
                {'>>'}
              </Button>
            </Col>
            <Col className="text-end">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="form-select w-auto d-inline-block"
              >
                {[5, 10, 20].map((size) => (
                  <option key={size} value={size}>
                    Show {size}
                  </option>
                ))}
              </select>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ReceiptsTable;
